import type { ReactNode } from 'react'

import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeaderCloseButton,
  ModalSheetHeaderShell,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './CreateJobDialog.module.css'
import { Icon } from '@/components/Icon'
import { pluralize } from '@/utils/pluralization-util'

export interface CreateJobDialogProps {
  showing: boolean
  onClose: () => unknown
  header: ReactNode
  children?: ReactNode
  footerFeedback?: ReactNode
  footerActions?: ReactNode
}

export function CreateJobDialog({
  showing,
  onClose,
  header,
  children,
  footerFeedback,
  footerActions,
}: CreateJobDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={onClose}
    >
      {header}

      <ModalSheetContent>
        <ModalSheetContentBlock testId="job-creation-dialog">
          {children}
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        {footerFeedback}
        <ModalSheetFooterSpacer />

        {footerActions}
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface CreateJobDialogHeaderProps {
  pager?: string | null
  title: string
  description: string
  draftTitle: string
}

export function CreateJobDialogHeader({
  pager,
  title,
  description,
  draftTitle,
}: CreateJobDialogHeaderProps) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        {pager && <div className={styles.pager}>{pager}</div>}

        <ModalSheetContentTitle testId={`draft-title-${draftTitle}`}>
          <div className={styles.headerContent}>{title}</div>
        </ModalSheetContentTitle>

        <div className={styles.contentDescription}>{description} </div>
      </div>

      <div className={styles.closeButtonContainer}>
        <ModalSheetHeaderCloseButton />
      </div>
    </ModalSheetHeaderShell>
  )
}

export interface CreateJobDialogBillingStepButtonsProps {
  onBack: () => unknown
  canViewDetail: boolean
  onViewDetail: () => unknown
  onDone: () => unknown
}

export function CreateJobDialogBillingStepButtons({
  onBack,
  canViewDetail,
  onViewDetail,
  onDone,
}: CreateJobDialogBillingStepButtonsProps) {
  return (
    <ModalSheetFooterButtonGroup>
      <Button
        label="Back"
        onPress={onBack}
        variant="default"
        testId="job-creation-dialog-go-to-questions-step-button"
      />

      {canViewDetail ? (
        <Button
          label="View Draft"
          variant="primary"
          onPress={onViewDetail}
          testId="job-creation-dialog-go-to-detail-button"
        />
      ) : (
        <Button label="Done" variant="primary" onPress={onDone} />
      )}
    </ModalSheetFooterButtonGroup>
  )
}

export interface CreateJobDialogBulkQuestionsStepButtonsProps {
  isBackShown: boolean
  onBack: () => unknown
  onNext: () => unknown
  nextLabel: string
}

export function CreateJobDialogBulkQuestionsStepButtons({
  isBackShown,
  onBack,
  onNext,
  nextLabel,
}: CreateJobDialogBulkQuestionsStepButtonsProps) {
  return (
    <ModalSheetFooterButtonGroup>
      {isBackShown && (
        <Button
          label="Back"
          onPress={onBack}
          variant="default"
          testId="job-creation-dialog-previous-button"
        />
      )}

      <Button
        label={nextLabel}
        onPress={onNext}
        variant="primary"
        testId="job-creation-dialog-next-button"
      />
    </ModalSheetFooterButtonGroup>
  )
}

export interface CreateJobDialogQuestionsStepButtonsProps {
  onClose: () => unknown
  isBillingRelevant?: boolean
  isBillingStepReady?: boolean
  onNext: () => unknown
  canViewDetail: boolean
  onViewDetail: () => unknown
}

export function CreateJobDialogQuestionsStepButtons({
  onClose,
  isBillingRelevant,
  isBillingStepReady,
  onNext,
  canViewDetail,
  onViewDetail,
}: CreateJobDialogQuestionsStepButtonsProps) {
  return (
    <ModalSheetFooterButtonGroup>
      <Button
        label="Close"
        onPress={onClose}
        variant="default"
        testId="job-creation-dialog-close-button"
      />

      {isBillingRelevant && (
        <Button
          label="Next"
          onPress={onNext}
          variant="primary"
          isDisabled={!isBillingStepReady}
          testId="job-creation-dialog-go-to-billing-step-button"
        />
      )}

      {canViewDetail && !isBillingRelevant && (
        <Button
          label="View Draft"
          variant="primary"
          onPress={onViewDetail}
          testId="job-creation-dialog-go-to-detail-button"
        />
      )}
    </ModalSheetFooterButtonGroup>
  )
}

export interface CreateJobDialogUnansweredFeedbackProps {
  isShown: boolean
  count: number
}

export function CreateJobDialogUnansweredFeedback({
  isShown,
  count,
}: CreateJobDialogUnansweredFeedbackProps) {
  return (
    <ModalSheetFooterButtonGroup>
      {isShown && (
        <div className={styles.unansweredFeedback}>
          <div className={styles.unansweredFeedbackIcon}>
            <Icon size={17} name="info" />
          </div>
          <div className={styles.unansweredFeedbackText}>
            {`${count} required ${pluralize('answer', count)} pending`}
          </div>
        </div>
      )}
    </ModalSheetFooterButtonGroup>
  )
}
